import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  Search,
  ClipboardList,
  Download,
  X,
  RefreshCw,
  FileDown,
  Pencil,
  Trash2,
  UserPlus,
  Signature,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { adminApi, getToken, API } from "@/lib/auth";
import { logError } from "@/lib/log";
import AttendanceEditDialog from "@/components/AttendanceEditDialog";

const ALL_COURSES = "__all__";
// Hasta que el gestor elige un curso no se muestra ninguna asistencia.
const NO_COURSE = "";

export default function AttendanceList() {
  const [courses, setCourses] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState(NO_COURSE);
  const [filterDni, setFilterDni] = useState("");
  const [viewer, setViewer] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // 2026-06-10: admin tambien puede capturar/eliminar firmas.
  // signTarget = { attendance, session: 1|2 } | null
  const [signTarget, setSignTarget] = useState(null);
  const [signSaving, setSignSaving] = useState(false);
  const [clearSignatureTarget, setClearSignatureTarget] = useState(null);

  const handleAdminEdit = async (id, payload) => {
    const res = await adminApi.put(`/attendance/${id}`, payload);
    setAttendances((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...res.data } : a))
    );
  };

  const handleAdminDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/attendance/${deleteTarget.id}`);
      setAttendances((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      toast.success("Asistencia eliminada.");
      setDeleteTarget(null);
    } catch (e) {
      logError("No se pudo eliminar la asistencia", e);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  // 2026-06-10: el admin puede capturar la firma (sesion 1 o 2) en su movil
  // o sustituir una ya existente.
  const handleAdminSign = async (dataUrl) => {
    if (!signTarget?.attendance) return;
    setSignSaving(true);
    const field = signTarget.session >= 2
      ? `signature_session${signTarget.session}_base64`
      : "signature_base64";
    try {
      const res = await adminApi.put(
        `/attendance/${signTarget.attendance.id}`,
        { [field]: dataUrl }
      );
      setAttendances((prev) =>
        prev.map((a) => (a.id === signTarget.attendance.id ? { ...a, ...res.data } : a))
      );
      toast.success(
        signTarget.session >= 2
          ? `Firma de la ${signTarget.session}ª sesión guardada.`
          : "Firma guardada."
      );
      setSignTarget(null);
    } catch (e) {
      logError("No se pudo guardar la firma", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar la firma.");
    } finally {
      setSignSaving(false);
    }
  };

  // Elimina UNA firma concreta (sesion 1 o 2) sin tocar la asistencia.
  const handleAdminClearSignature = async () => {
    if (!clearSignatureTarget) return;
    const { attendance, session } = clearSignatureTarget;
    const field = session >= 2
      ? `signature_session${session}_base64`
      : "signature_base64";
    try {
      const res = await adminApi.put(
        `/attendance/${attendance.id}`,
        { [field]: "" }
      );
      setAttendances((prev) =>
        prev.map((a) => (a.id === attendance.id ? { ...a, ...res.data } : a))
      );
      toast.success("Firma eliminada.");
      setClearSignatureTarget(null);
      setViewer(null);
    } catch (e) {
      logError("No se pudo eliminar la firma", e);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la firma.");
    }
  };
  const [exporting, setExporting] = useState(false);
  // Diálogo "Añadir asistencia" — alta manual desde Gestión.
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [askSignatureFor, setAskSignatureFor] = useState(null);
  const [addForm, setAddForm] = useState({
    course_id: "",
    name: "",
    surname: "",
    dni: "",
    profession: "",
  });

  const handleAddSubmit = async () => {
    const courseId = addForm.course_id;
    if (!courseId) {
      toast.error("Selecciona un curso.");
      return;
    }
    if (!addForm.name.trim() || !addForm.surname.trim() || !addForm.dni.trim()) {
      toast.error("Nombre, apellidos y DNI son obligatorios.");
      return;
    }
    const course = courses.find((c) => c.id === courseId);
    setAddSaving(true);
    try {
      const res = await adminApi.post(`/admin/attendance`, {
        course_id: courseId,
        course_name: course?.name || "",
        name: addForm.name.trim().toUpperCase(),
        surname: addForm.surname.trim().toUpperCase(),
        dni: addForm.dni.trim().toUpperCase(),
        profession: addForm.profession.trim().toUpperCase(),
        // signature/consent intencionalmente vacíos: el endpoint admin no los exige.
        signature_base64: "",
        consent_accepted: true,
        image_consent_denied: false,
      });
      setAttendances((prev) => [res.data, ...prev]);
      toast.success("Asistencia añadida.");
      setAddOpen(false);
      // Abre el SignaturePad para que el gestor recoja la firma del alumno.
      setAskSignatureFor(res.data);
      setAddForm({
        course_id: "", name: "", surname: "", dni: "", profession: "",
      });
    } catch (e) {
      logError("No se pudo añadir la asistencia", e);
      toast.error(e?.response?.data?.detail || "No se pudo añadir.");
    } finally {
      setAddSaving(false);
    }
  };

  // Guarda la firma del alumno tras "Añadir + pedir firma".
  const handleSaveStudentSignature = async (signature_base64) => {
    if (!askSignatureFor) return;
    try {
      const res = await adminApi.put(
        `/attendance/${askSignatureFor.id}`,
        { signature_base64 }
      );
      setAttendances((prev) =>
        prev.map((a) => (a.id === askSignatureFor.id ? res.data : a))
      );
      toast.success("Firma registrada.");
    } catch (e) {
      logError("No se pudo guardar la firma", e);
      toast.error(e?.response?.data?.detail || "Error al guardar la firma.");
    } finally {
      setAskSignatureFor(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, attRes] = await Promise.all([
        adminApi.get(`/courses`),
        adminApi.get(`/attendance`),
      ]);
      setCourses(
        [...(coursesRes.data || [])].sort((a, b) =>
          String(a?.name || a?.client_name || "").localeCompare(
            String(b?.name || b?.client_name || ""),
            "es",
            { sensitivity: "base" }
          )
        )
      );
      setAttendances(attRes.data || []);
    } catch (e) {
      logError("No se pudieron cargar las asistencias", e);
      toast.error("No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    return attendances.filter((a) => {
      if (filterCourse === NO_COURSE) return false;
      if (filterCourse !== ALL_COURSES && a.course_id !== filterCourse) return false;
      if (
        filterDni.trim() &&
        !a.dni.toUpperCase().includes(filterDni.trim().toUpperCase())
      )
        return false;
      return true;
    });
  }, [attendances, filterCourse, filterDni]);

  // 2026-06-10: lookup curso por id, para saber si necesita 1 o 2 firmas.
  const coursesById = useMemo(() => {
    const m = {};
    for (const c of courses) m[c.id] = c;
    return m;
  }, [courses]);

  // Devuelve cuantas firmas necesita el curso de la asistencia (1, 2 ó 3 —
  // los cursos INICIAL de 3 dias son exclusivos de MURCIA) y cuantas tiene.
  const signatureStatus = (att) => {
    const c = coursesById[att.course_id];
    const isInicial = (c?.course_type || "").toLowerCase() === "inicial";
    const courseDays = parseInt(c?.days, 10) || 1;
    const isInicial2d = isInicial && courseDays >= 2;
    const isInicial3d = isInicial && courseDays >= 3;
    const required = isInicial3d ? 3 : (isInicial2d ? 2 : 1);
    const has1 = !!att.signature_base64;
    const has2 = !!att.signature_session2_base64;
    const has3 = !!att.signature_session3_base64;
    const present =
      (has1 ? 1 : 0) +
      (isInicial2d && has2 ? 1 : 0) +
      (isInicial3d && has3 ? 1 : 0);
    return {
      required, present, has1, has2, has3,
      isInicial2d, isInicial3d,
      complete: present >= required,
    };
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const downloadSignature = (att) => {
    const a = document.createElement("a");
    a.href = att.signature_base64;
    const safeName = `${att.dni}_${att.course_name}`
      .replace(/[^A-Za-z0-9_-]+/g, "_")
      .slice(0, 80);
    a.download = `firma_${safeName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportPdf = async () => {
    if (filtered.length === 0) {
      toast.error("No hay asistencias para exportar con los filtros actuales.");
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterCourse !== ALL_COURSES && filterCourse !== NO_COURSE)
        params.append("course_id", filterCourse);
      if (filterDni.trim()) params.append("dni", filterDni.trim().toUpperCase());

      const res = await fetch(`${API}/attendance/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to read filename from Content-Disposition
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "asistencias.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado.");
    } catch (e) {
      toast.error("Error al exportar el PDF.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section data-testid="attendance-list">
      <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6">
        <div className="flex-1">
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            Asistencias
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            {filtered.length} de {attendances.length}{" "}
            {attendances.length === 1 ? "registro" : "registros"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setAddOpen(true)}
            className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
            data-testid="attendance-add-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Añadir
          </Button>
          <Button
            variant="outline"
            onClick={fetchData}
            className="h-11 border border-zinc-300 hover:bg-zinc-50"
            data-testid="attendance-refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button
            onClick={exportPdf}
            disabled={exporting || filtered.length === 0}
            className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
            data-testid="attendance-export-pdf-button"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {exporting ? "Exportando…" : "Exportar PDF"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <label className="block text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
            Curso
          </label>
          <Select value={filterCourse} onValueChange={setFilterCourse}>
            <SelectTrigger
              className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
              data-testid="attendance-course-filter"
            >
              <SelectValue placeholder="Selecciona un curso…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_COURSES}>Todos los cursos</SelectItem>
              {courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
            Buscar por DNI
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              value={filterDni}
              onChange={(e) => setFilterDni(e.target.value.toUpperCase())}
              placeholder="12345678A"
              className="h-11 pl-10 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 tracking-wider"
              data-testid="attendance-dni-filter"
            />
            {filterDni && (
              <button
                onClick={() => setFilterDni("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                data-testid="attendance-dni-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : filterCourse === NO_COURSE ? (
        <div
          className="bg-white border-2 border-dashed border-zinc-300 rounded-lg p-12 text-center"
          data-testid="attendance-pick-course"
        >
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <ClipboardList className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Elige un curso
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            Selecciona un curso en el desplegable para ver sus asistencias.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <ClipboardList className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Sin asistencias
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            {attendances.length === 0
              ? "Cuando un alumno firme su asistencia aparecerá aquí."
              : "No hay resultados con los filtros aplicados."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
            <div className="col-span-3">Alumno</div>
            <div className="col-span-2">DNI</div>
            <div className="col-span-3">Curso</div>
            <div className="col-span-1">Estado</div>
            <div className="col-span-1">Fecha</div>
            <div className="col-span-2 text-right">Acciones</div>
          </div>
          {filtered.map((a) => {
            const st = signatureStatus(a);
            return (
            <div
              key={a.id}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-zinc-100 last:border-b-0 items-center border-l-4 ${
                st.complete ? "border-l-emerald-500" : "border-l-rose-500"
              }`}
              data-testid={`attendance-row-${a.id}`}
            >
              <div className="md:col-span-3">
                <div className="font-semibold text-zinc-950">
                  {a.name} {a.surname}
                </div>
              </div>
              <div className="md:col-span-2 text-sm font-mono text-zinc-700 tracking-wider">
                {a.dni}
              </div>
              <div className="md:col-span-3 text-sm text-zinc-700 truncate">
                {a.course_name}
              </div>
              <div
                className="md:col-span-1"
                data-testid={`attendance-status-${a.id}`}
              >
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold border-2 ${
                    st.complete
                      ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                      : "bg-rose-50 border-rose-300 text-rose-800"
                  }`}
                  title={
                    st.complete
                      ? "Todas las firmas presentes"
                      : `Faltan ${st.required - st.present} firma(s)`
                  }
                >
                  {st.complete ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5" />
                  )}
                  <span className="font-mono">
                    {st.present}/{st.required}
                  </span>
                </div>
              </div>
              <div className="md:col-span-1 text-xs text-zinc-500">
                {formatDateTime(a.created_at)}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-1.5 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditTarget(a)}
                  className="h-9 px-2 border border-zinc-300 hover:bg-zinc-50"
                  data-testid={`edit-attendance-${a.id}`}
                  title="Editar datos"
                  aria-label="Editar datos del alumno"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (st.has1) setViewer(a);
                    else setSignTarget({ attendance: a, session: 1 });
                  }}
                  className="h-9 border border-zinc-300 hover:bg-zinc-50"
                  data-testid={`view-signature-${a.id}`}
                  title="Ver firma"
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Ver
                </Button>
                {/* Firmar (sesion 1 o 2 segun lo que falte) */}
                {!st.has1 && (
                  <Button
                    size="sm"
                    onClick={() => setSignTarget({ attendance: a, session: 1 })}
                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`admin-sign1-${a.id}`}
                    title="Capturar firma en este móvil"
                  >
                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                    Firmar
                  </Button>
                )}
                {st.isInicial2d && st.has1 && !st.has2 && (
                  <Button
                    size="sm"
                    onClick={() => setSignTarget({ attendance: a, session: 2 })}
                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`admin-sign2-${a.id}`}
                    title="Capturar firma de la 2ª sesión"
                  >
                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                    Firma 2ª
                  </Button>
                )}
                {st.isInicial3d && st.has1 && st.has2 && !st.has3 && (
                  <Button
                    size="sm"
                    onClick={() => setSignTarget({ attendance: a, session: 3 })}
                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`admin-sign3-${a.id}`}
                    title="Capturar firma de la 3ª sesión"
                  >
                    <Signature className="h-3.5 w-3.5 mr-1.5" />
                    Firma 3ª
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(a)}
                  className="h-9 px-2 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                  data-testid={`delete-attendance-${a.id}`}
                  title="Eliminar asistencia"
                  aria-label="Eliminar asistencia"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent
          className="sm:max-w-2xl"
          data-testid="signature-viewer-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              Firma de asistencia
            </DialogTitle>
            <DialogDescription>
              {viewer ? (
                <>
                  <span className="font-semibold text-zinc-950">
                    {viewer.name} {viewer.surname}
                  </span>{" "}
                  · DNI {viewer.dni} · {viewer.course_name} ·{" "}
                  {formatDateTime(viewer.created_at)}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {viewer && (
            <div className="space-y-4">
              <div className="bg-white border-2 border-zinc-200 rounded-md p-3">
                <img
                  src={viewer.signature_base64}
                  alt={`Firma de ${viewer.name} ${viewer.surname}`}
                  className="w-full h-auto max-h-[60vh] object-contain"
                  data-testid="signature-image"
                />
              </div>
              {/* Si es inicial 2 dias y tiene firma de la 2ª sesion, mostrarla */}
              {viewer.signature_session2_base64 && (
                <div className="bg-white border-2 border-zinc-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                    Firma 2ª sesión
                  </div>
                  <img
                    src={viewer.signature_session2_base64}
                    alt={`Firma 2ª sesión de ${viewer.name} ${viewer.surname}`}
                    className="w-full h-auto max-h-[40vh] object-contain"
                  />
                </div>
              )}
              {viewer.signature_session3_base64 && (
                <div className="bg-white border-2 border-zinc-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                    Firma 3ª sesión
                  </div>
                  <img
                    src={viewer.signature_session3_base64}
                    alt={`Firma 3ª sesión de ${viewer.name} ${viewer.surname}`}
                    className="w-full h-auto max-h-[40vh] object-contain"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setViewer(null)}
                  className="border border-zinc-300"
                  data-testid="signature-viewer-close"
                >
                  Cerrar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSignTarget({ attendance: viewer, session: 1 });
                    setViewer(null);
                  }}
                  className="border border-zinc-300"
                  data-testid="admin-resign-signature"
                >
                  <Signature className="h-4 w-4 mr-2" />
                  Re-firmar
                </Button>
                <Button
                  onClick={() =>
                    setClearSignatureTarget({ attendance: viewer, session: 1 })
                  }
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="admin-clear-signature-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar firma
                </Button>
                <Button
                  onClick={() => downloadSignature(viewer)}
                  className="bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="signature-download-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PNG
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SignaturePad: el admin captura/sustituye la firma en su movil */}
      {signTarget && (
        <SignaturePad
          onCancel={() => setSignTarget(null)}
          onSave={handleAdminSign}
          saving={signSaving}
          courseName={
            (coursesById[signTarget.attendance?.course_id]?.name ||
              signTarget.attendance?.course_name ||
              "") +
            (signTarget.session === 2 ? " · 2ª sesión" : "")
          }
          studentName={`${signTarget.attendance?.name || ""} ${signTarget.attendance?.surname || ""}`.trim()}
        />
      )}

      {/* Confirmar borrado de firma individual */}
      <AlertDialog
        open={!!clearSignatureTarget}
        onOpenChange={(o) => !o && setClearSignatureTarget(null)}
      >
        <AlertDialogContent data-testid="admin-clear-signature-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar firma?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Se eliminará la firma{" "}
                {clearSignatureTarget?.session === 2 ? "de la 2ª sesión" : "registrada"}{" "}
                de{" "}
                <span className="font-semibold text-zinc-950">
                  {clearSignatureTarget?.attendance?.name}{" "}
                  {clearSignatureTarget?.attendance?.surname}
                </span>
                . La asistencia se mantiene; sólo se borra esa firma.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-clear-signature-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdminClearSignature}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-clear-signature-confirm"
            >
              Eliminar firma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AttendanceEditDialog
        attendance={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleAdminEdit}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-attendance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar asistencia?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  Se eliminará la asistencia firmada de{" "}
                  <span className="font-semibold text-zinc-950">
                    {deleteTarget?.name} {deleteTarget?.surname}
                  </span>{" "}
                  (DNI {deleteTarget?.dni}). Esta acción no se puede deshacer.
                </div>
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠ Si vas a eliminar una asistencia ya recogida por una hoja de
                  control de firmas oficial, asegúrate de regenerar dicho
                  documento.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-attendance-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdminDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-attendance-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo "Añadir asistencia" — alta manual desde Gestión */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="attendance-add-dialog"
        >
          <DialogHeader>
            <DialogTitle>Añadir asistencia</DialogTitle>
            <DialogDescription>
              Da de alta a un alumno en un curso. La firma del alumno y el
              consentimiento RGPD se pueden recoger más tarde, esta alta
              sirve para que aparezca en el listado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-zinc-700">
                Curso *
              </label>
              <Select
                value={addForm.course_id}
                onValueChange={(v) =>
                  setAddForm((f) => ({ ...f, course_id: v }))
                }
              >
                <SelectTrigger
                  className="h-11 border border-zinc-300"
                  data-testid="attendance-add-course"
                >
                  <SelectValue placeholder="Selecciona un curso" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      data-testid={`attendance-add-course-opt-${c.id}`}
                    >
                      {c.name || c.client_name}
                      {c.date1 ? ` · ${c.date1}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">
                  Nombre *
                </label>
                <Input
                  value={addForm.name}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="attendance-add-name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">
                  Apellidos *
                </label>
                <Input
                  value={addForm.surname}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, surname: e.target.value.toUpperCase() }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="attendance-add-surname"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700">
                  DNI *
                </label>
                <Input
                  value={addForm.dni}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, dni: e.target.value.toUpperCase() }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="attendance-add-dni"
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label className="text-sm font-semibold text-zinc-700">
                  Profesión
                </label>
                <Input
                  value={addForm.profession}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, profession: e.target.value.toUpperCase() }))
                  }
                  className="h-11 border border-zinc-300"
                  data-testid="attendance-add-profession"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              className="h-11 border border-zinc-300 hover:bg-zinc-50"
              data-testid="attendance-add-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddSubmit}
              disabled={addSaving}
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="attendance-add-submit"
            >
              {addSaving ? "Guardando…" : "Añadir + Pedir firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {askSignatureFor && (
        <SignaturePad
          onCancel={() => setAskSignatureFor(null)}
          onSave={handleSaveStudentSignature}
          saving={false}
          courseName={
            courses.find((c) => c.id === askSignatureFor.course_id)?.name ||
            courses.find((c) => c.id === askSignatureFor.course_id)?.client_name ||
            ""
          }
          studentName={`${askSignatureFor.name} ${askSignatureFor.surname}`.trim()}
        />
      )}
    </section>
  );
}
